import React from "react";
import { Link } from "@inertiajs/react";
import {
  Box,
  Stack,
  Group,
  Image,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import {
  IconFileText,
  IconPlayerPlay,
  IconMicrophone,
  IconArrowUpRight,
} from "@tabler/icons-react";
import { Card } from "./UI/Card";
import {
  Heading3,
  MainText,
  DimmedText,
  Caption,
} from "./UI/Typography";
import { Badge } from "./UI/Badge";
import type { Post, PostFormat } from "../data/posts";
import { postUrl, formatDuration } from "../data/posts";

interface PostCardProps {
  post: Post;
  /**
   * When set, renders a small "From /pillar" badge indicating this post's
   * canonical home is a different pillar than the one it's being shown on.
   * Used by "Related from other pillars" strips.
   */
  fromPillarBadge?: boolean;
}

function formatIcon(type: PostFormat["type"]) {
  switch (type) {
    case "article":
      return IconFileText;
    case "video":
      return IconPlayerPlay;
    case "podcast":
      return IconMicrophone;
  }
}

function formatLabel(f: PostFormat): string {
  switch (f.type) {
    case "article":
      return `${f.readTimeMin} min read`;
    case "video":
      return formatDuration(f.durationSec);
    case "podcast":
      return formatDuration(f.durationSec);
  }
}

export const PostCard: React.FC<PostCardProps> = ({ post, fromPillarBadge }) => {
  const theme = useMantineTheme();
  const other = theme.other as Record<string, string>;
  const [hovered, setHovered] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);

  const formattedDate = new Date(post.publishedAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const glassHover = {
    transform: "translateY(-6px)",
    boxShadow:
      "0 20px 48px rgba(212,85,43,0.18), 0 0 0 1px rgba(212,85,43,0.20), inset 0 1px 0 rgba(255,255,255,0.1)",
    border: "1px solid rgba(212,85,43,0.20)",
  };

  const glassRest = {
    transform: "translateY(0)",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.07), 0 8px 32px rgba(0,0,0,0.4)",
    border: "1px solid rgba(212,85,43,0.12)",
  };

  const glassActive = {
    transform: "translateY(-3px) scale(0.99)",
    boxShadow:
      "0 10px 24px rgba(212,85,43,0.15), 0 0 0 1px rgba(212,85,43,0.18), inset 0 1px 0 rgba(255,255,255,0.08)",
    border: "1px solid rgba(212,85,43,0.18)",
  };

  return (
    <UnstyledButton
      component={Link}
      href={postUrl(post)}
      style={{
        textDecoration: "none",
        display: "block",
        height: "100%",
        width: "100%",
      }}
    >
      <Card
        padding={0}
        radius="md"
        h="100%"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => {
          setHovered(false);
          setPressed(false);
        }}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        style={{
          cursor: "pointer",
          overflow: "hidden",
          ...(pressed ? glassActive : hovered ? glassHover : glassRest),
        }}
      >
        <Stack gap={0} h="100%">
          {post.coverImage && (
            <Image
              src={post.coverImage}
              alt={post.title}
              h={180}
              fit="cover"
              style={{ display: "block" }}
            />
          )}

          <Stack gap="sm" p="lg" h="100%" style={{ flex: 1 }}>
            {(fromPillarBadge || post.tags.length > 0) && (
              <Group gap="xs" wrap="wrap">
                {fromPillarBadge && (
                  <Badge variant="outline" size="sm" radius="md">
                    From /{post.pillar}
                  </Badge>
                )}
                {post.tags.slice(0, 3).map((tag) => (
                  <Badge key={tag} variant="light" size="sm" radius="md">
                    {tag}
                  </Badge>
                ))}
              </Group>
            )}

            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Heading3 style={{ margin: 0, flex: 1 }}>{post.title}</Heading3>
              <IconArrowUpRight
                size={18}
                style={{ color: other.textDimmed, flexShrink: 0, marginTop: 4 }}
              />
            </Group>

            <MainText
              size="sm"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                color: other.textDimmed,
              }}
            >
              {post.excerpt}
            </MainText>

            <Box style={{ marginTop: "auto" }}>
              <Group
                gap="md"
                justify="space-between"
                align="center"
                wrap="wrap"
              >
                <DimmedText size="xs">{formattedDate}</DimmedText>
                <Group gap="sm" wrap="nowrap">
                  {post.formats.map((f) => {
                    const Icon = formatIcon(f.type);
                    return (
                      <Group key={f.type} gap={4} wrap="nowrap">
                        <Icon size={13} style={{ color: other.textDimmed }} />
                        <Caption style={{ margin: 0 }}>
                          {formatLabel(f)}
                        </Caption>
                      </Group>
                    );
                  })}
                </Group>
              </Group>
            </Box>
          </Stack>
        </Stack>
      </Card>
    </UnstyledButton>
  );
};
